import { ConfirmDialog, NewConvergeButton } from '../../../presentation';
import {
  QuoteStatus,
  SubmissionStatus,
  SubmissionSubStatus,
  useBindQuoteMutation,
  useUndoQuotePackageWithoutRefetchMutation,
  useUpsertSubmissionMutation,
} from '@converge/graphql';
import { useCallback, useState } from 'react';

import { useConvergeSnackBar } from '../../../contexts';
import { useUnderwritingSubmissionStateContext } from '../underwriting.submission.context';

export interface IEditSubmissionButtonProps {
  submissionUuid: string;
  pageType: 'Overview' | 'Questionnaire';
}

export const EditSubmissionButton = ({
  submissionUuid,
  pageType,
}: IEditSubmissionButtonProps) => {
  const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { notify } = useConvergeSnackBar();

  const { upsertSubmission } = useUpsertSubmissionMutation();
  const { undoQuotePackage } = useUndoQuotePackageWithoutRefetchMutation();
  const { bindQuote } = useBindQuoteMutation();

  const {
    info: { boundQuote },
  } = useUnderwritingSubmissionStateContext();

  const handleUndoQuotePackage = useCallback(
    (submissionUuid: string) => {
      return undoQuotePackage({
        variables: {
          submissionUuid: submissionUuid,
        },
      });
    },
    [undoQuotePackage]
  );

  const handleUnBindQuote = useCallback(
    (submissionUuid: string) => {
      return bindQuote({
        variables: {
          boundQuoteUuid: boundQuote.uuid,
          boundQuoteStatus: QuoteStatus.Quoted,
          submissionDetails: {
            uuid: submissionUuid,
          },
          coverages: [],
          endorsements: [],
        },
      });
    },
    [bindQuote, boundQuote?.uuid]
  );

  const onEditEvent = useCallback(
    (submissionUuid: string) => {
      return upsertSubmission({
        variables: {
          details: {
            uuid: submissionUuid ?? '',
            status: SubmissionStatus.Evaluation,
            subStatus: SubmissionSubStatus.EvaluationEditable,
          },
        },
      });
    },
    [upsertSubmission]
  );

  return (
    <>
      <NewConvergeButton
        loading={isLoading}
        variant="contained"
        onClick={() => setShowConfirmDialog(true)}
        label="Edit"
      />
      <ConfirmDialog
        show={showConfirmDialog}
        title="Warning"
        content={`Editing this submission's overview or questionnaire will invalidate previously created quotes and associated documentation. Are you sure you want to edit the ${pageType.toLowerCase()}?`}
        onCancel={() => setShowConfirmDialog(false)}
        onConfirm={async () => {
          try {
            setShowConfirmDialog(false);
            setIsLoading(true);
            if (boundQuote && boundQuote.uuid) {
              await handleUnBindQuote(submissionUuid);
            }
            await handleUndoQuotePackage(submissionUuid);

            await onEditEvent(submissionUuid);
            setIsLoading(false);
          } catch (cause) {
            setIsLoading(false);
            notify(
              cause instanceof Error
                ? `Error saving: ${cause.message}`
                : 'Error saving',
              {
                variant: 'error',
              }
            );
          }
        }}
      />
    </>
  );
};
